import { useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@crackpoint/shared/components';
import { useGameStore } from './state/gameStore';
import { useSettingsStore } from './state';
import { getInstantGames, detectPlatform } from './utils/instantGames';
import { createAdapter } from '@crackpoint/shared/adapters';
import { useGameLoop } from './hooks/useGameLoop';
import { API_BASE_URL } from './utils/constants';
import {
  MenuScreen,
  GameScreen,
} from './components/layout';
import { RoomCompleteScreen, GameOverScreen, DevBanner } from '@crackpoint/shared/components';

export default function App() {
  const [isInitialised, setIsInitialised] = useState(false);

  const screen = useGameStore((s) => s.screen);
  const run = useGameStore((s) => s.run);
  const completionResult = useGameStore((s) => s.completionResult);
  const attempts = useGameStore((s) => s.attempts);
  const solution = useGameStore((s) => s.solution);
  const setScreen = useGameStore((s) => s.setScreen);
  const themeId = useSettingsStore((s) => s.themeId);

  const platform = useMemo(() => detectPlatform(), []);
  const adapterMode = platform === 'none' ? 'server' : 'client';

  const adapter = useMemo(
    () => createAdapter(adapterMode, adapterMode === 'server' ? API_BASE_URL : undefined),
    [adapterMode],
  );

  const gameLoop = useGameLoop(adapter);

  useEffect(() => {
    const initApp = async () => {
      try {
        const instantGames = getInstantGames();
        await instantGames.initialise();
        await instantGames.startGame();
      } catch (error) {
        console.warn('Instant games SDK unavailable:', error);
      }
      setIsInitialised(true);
    };

    initApp();
  }, []);

  if (!isInitialised) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#0a0e14',
          color: '#f0f0f5',
          fontFamily: "'Courier New', 'Lucida Console', monospace",
        }}
      >
        Initialising...
      </div>
    );
  }

  return (
    <ThemeProvider defaultThemeId={themeId}>
      <DevBanner apiUrl={API_BASE_URL} />
      {screen === 'title' && (
        <MenuScreen
          onStartGame={gameLoop.startRun}
          onShowLeaderboard={() => setScreen('leaderboard' as never)}
          onShowSettings={() => setScreen('settings' as never)}
          isLoading={gameLoop.isLoading}
        />
      )}

      {screen === 'room' && (
        <GameScreen
          gameLoop={gameLoop}
          adapter={adapter}
        />
      )}

      {screen === 'roomComplete' && completionResult && (
        <RoomCompleteScreen
          result={completionResult}
          solution={solution ?? []}
          attemptCount={attempts.length}
          onAdvance={gameLoop.advanceToNextRoom}
        />
      )}

      {screen === 'gameOver' && run && (
        <GameOverScreen
          run={run}
          onRestart={gameLoop.returnToTitle}
        />
      )}
    </ThemeProvider>
  );
}
